import axios from 'axios'

/**
 * Recarga el proyecto actual en Vuex con la misma API que select-group
 * (GET /api/v2/user/projects?withConfigs=1).
 * Solo actualiza si el proyecto en store sigue siendo el solicitado.
 *
 * @param {object} store
 * @param {number|string} projectId
 * @returns {Promise<object|null>} proyecto fresco o null
 */
export async function refreshCurrentProjectInStore(store, projectId) {
  if (!store || projectId == null) {
    return null
  }

  const current = store.getters['app/project']
  const currentId = current && current.id != null ? Number(current.id) : null
  const targetId = Number(projectId)

  if (currentId == null || currentId !== targetId) {
    return null
  }

  const company = store.getters['app/company']
  const group = store.getters['app/group']
  const groupId = (group && group.id)
    || (current.groups && current.groups[0] && current.groups[0].id)
    || null

  const params = {
    withConfigs: 1,
    showArchive: 1,
  }

  if (company && company.id) {
    params.companyId = company.id
  }

  if (groupId) {
    params.group = groupId
  }

  const { data } = await axios.get('/api/v2/user/projects', { params })
  const freshProject = (data.projects || []).find(
    (project) => Number(project.id) === targetId,
  )

  if (!freshProject) {
    return null
  }

  // Revalidar: el proyecto activo no cambió durante la petición
  const stillCurrent = store.getters['app/project']
  if (!stillCurrent || Number(stillCurrent.id) !== targetId) {
    return null
  }

  await store.dispatch('app/setProject', { project: freshProject })
  return freshProject
}

/**
 * Merge parcial de claves en project.configs (PATCH).
 * No pisa claves no enviadas. value null elimina la clave.
 *
 * Si se pasa store, recarga el proyecto actual vía /user/projects (como select-group).
 *
 * @param {number|string} projectId
 * @param {Record<string, any>} configsPatch
 * @param {{ store?: object }} [options]
 * @returns {Promise<object>} configs resultantes
 */
export async function mergeProjectConfigs(projectId, configsPatch, options = {}) {
  const { data } = await axios.patch(`/api/v2/project/${projectId}/configs`, {
    configs: configsPatch,
  })

  if (!(data && data.success)) {
    throw new Error('merge_configs_failed')
  }

  const { store } = options
  let configs = data.configs || {}

  if (store) {
    const freshProject = await refreshCurrentProjectInStore(store, projectId)
    if (freshProject && freshProject.configs) {
      configs = freshProject.configs
    }
  }

  return configs
}
